import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Inject, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { WindowRef } from '@spartacus/core';
import { Observable, Subject, fromEvent } from 'rxjs';
import { distinctUntilChanged, filter, map, throttleTime } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class KeyboardService {
  private keyboardEvents$ = new Subject<boolean>();
  private destroyRef = inject(DestroyRef);
  private initialViewportHeight: number;
  private readonly threshold = 45;

  constructor(
    private ngZone: NgZone,
    private winRef: WindowRef,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeKeyboardDetection();

      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.ngZone.run(() => this.keyboardEvents$.next(false));
        });
    }
  }

  private initializeKeyboardDetection(): void {
    const nativeWindow = this.winRef.nativeWindow;

    if (!nativeWindow || !nativeWindow.visualViewport) {
      return;
    }

    const updateInitialHeight = (): void => {
      this.initialViewportHeight = nativeWindow.visualViewport?.height || 0;
    };

    updateInitialHeight();

    this.ngZone.runOutsideAngular(() => {
      let ignoreNextResize = false;

      if (nativeWindow.visualViewport) {
        fromEvent(nativeWindow, 'orientationchange')
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            ignoreNextResize = true;
            setTimeout(updateInitialHeight, 300);
          });

        fromEvent(nativeWindow.visualViewport, 'resize')
          .pipe(
            throttleTime(50),
            map(() => nativeWindow?.visualViewport?.height || 0),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe((currentHeight) => {
            if (ignoreNextResize) {
              ignoreNextResize = false;

              return;
            }
            const isOpen = currentHeight + this.threshold < this.initialViewportHeight;
            this.ngZone.run(() => {
              this.keyboardEvents$.next(isOpen);
            });
          });
      }
    });
  }

  get keyboardState(): Observable<boolean> {
    return this.keyboardEvents$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef));
  }
}
